import store from 'dashboard/store';
import { emitter } from 'shared/helpers/mitt';
import { extractBase64ImagesAndUpload } from 'dashboard/helper/imageBase64Uploader';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { findOrCreateContacts } from 'dashboard/helper/contactCreationHelper';
import { logError } from 'dashboard/helper/logger';
import { getAccountIdFromLocation } from 'dashboard/helper/browserNavigation';

export const EMAIL_SEND_STATUS_EVENT = 'EMAIL_SEND_STATUS';

const runEmailSendBestEffortSideEffect = (callback) => {
  try {
    callback();
  } catch {
    // UI/event listeners must not turn an accepted email into a failed send.
  }
};

const isCapturedAccountCurrent = (accountId) => {
  const currentAccountId =
    store?.getters?.getCurrentAccountId ?? getAccountIdFromLocation();
  return (
    currentAccountId == null || String(currentAccountId) === String(accountId)
  );
};

/**
 * Uploads base64 images in background, patches the payload, and dispatches
 * the message send via the Vuex store. Emits status events so
 * EmailSendingIndicator can reflect progress.
 *
 * @param {string} rawHtml  - HTML snapshot with base64 images
 * @param {Object} payload  - message payload from getMessagePayload()
 * @param {number} accountId
 */
export async function backgroundEmailSend(rawHtml, payload, accountId) {
  try {
    runEmailSendBestEffortSideEffect(() =>
      emitter.emit(EMAIL_SEND_STATUS_EVENT, { status: 'uploading' })
    );

    // Fire-and-forget: create contacts for TO + CC emails
    const toEmails = payload.contentAttributes?.email?.to || [];
    const ccEmails = payload.contentAttributes?.email?.cc || [];
    const allEmails = [...toEmails, ...ccEmails];
    if (allEmails.length > 0 && payload.inbox_id) {
      findOrCreateContacts(allEmails, {
        inboxId: payload.inbox_id,
        accountId,
      });
    }

    const { html, uploadCount, errorCount } =
      await extractBase64ImagesAndUpload(rawHtml, accountId);

    // Total failure: only block if there were images to upload AND all failed.
    // Images stripped from blockquotes don't count (they return uploadCount=0, errorCount=0
    // when there are no new-content images).
    if (errorCount > 0 && uploadCount === 0) {
      runEmailSendBestEffortSideEffect(() =>
        emitter.emit(EMAIL_SEND_STATUS_EVENT, {
          status: 'error',
          errorType: 'total_failure',
        })
      );
      return false;
    }

    // Patch the payload with processed HTML (base64 replaced with URLs)
    payload.message = html;
    if (payload.contentAttributes?.email?.html_content) {
      payload.contentAttributes.email.html_content.full = html;
    }

    if (!isCapturedAccountCurrent(accountId)) {
      throw new Error('Account context changed before email send');
    }

    // Dispatch through Vuex
    await store.dispatch('createPendingMessageAndSend', {
      ...payload,
      accountId,
    });

    if (!isCapturedAccountCurrent(accountId)) {
      return true;
    }

    // Emit the completion event without forcing email threads to re-scroll.
    runEmailSendBestEffortSideEffect(() =>
      emitter.emit(BUS_EVENTS.MESSAGE_SENT)
    );

    runEmailSendBestEffortSideEffect(() =>
      emitter.emit(EMAIL_SEND_STATUS_EVENT, {
        status: 'sent',
        partialFailures: errorCount,
      })
    );

    return true;
  } catch (err) {
    logError('backgroundEmailSend failed:', err);
    if (isCapturedAccountCurrent(accountId)) {
      runEmailSendBestEffortSideEffect(() =>
        emitter.emit(EMAIL_SEND_STATUS_EVENT, { status: 'error' })
      );
    }
    return false;
  }
}
